// check ADR#3 for the motivation behind this module
import { generateGUID } from '@wix/cashier-common/dist/src/utils/guid/generateGUID';

interface Job<JobName> {
  id: string;
  name: JobName;
  promise: Promise<void>;
  resolve(): void;
  hasOwnPromise: boolean;
}

export class AsyncJobManager<JobName = string> {
  private jobsMap: Map<JobName, Job<JobName>> = new Map();

  private get jobs(): Job<JobName>[] {
    return Array.from(this.jobsMap.values());
  }

  add(name: JobName, promise?: Promise<any>) {
    this.remove(name);

    const id = generateGUID();
    let resolve: () => void;

    const jobPromise = new Promise<void>((_resolve) => {
      resolve = () => {
        _resolve();
      };
    });

    this.jobsMap.set(name, {
      id,
      name,
      promise: jobPromise,
      resolve,
      hasOwnPromise: !!promise,
    });

    promise?.then(() => {
      // to prohibit old promise from finishing the new one when both are using the same "name"
      if (this.jobsMap.get(name)?.id === id) {
        this.jobsMap.get(name).resolve();
      }
    });
  }

  remove(name: JobName) {
    if (this.jobsMap.has(name)) {
      // to release any "done" listeners that might be added before the job removal
      this.jobsMap.get(name).resolve();
      this.jobsMap.delete(name);
    }
  }

  finish(name: JobName) {
    if (this.jobsMap.has(name)) {
      if (this.jobsMap.get(name).hasOwnPromise) {
        console.warn(
          `Can't finish the job ("${name}") that has custom resolver`
        );
      } else {
        this.jobsMap.get(name).resolve();
      }
    }
  }

  async done(...names: JobName[]): Promise<void> {
    await Promise.all(
      names
        .filter((name) => this.jobsMap.has(name))
        .map((name) => this.jobsMap.get(name).promise)
    );
  }

  async doneAll(): Promise<void> {
    await this.done(...this.jobs.map(({ name }) => name));
  }
}
